import { StaticEntityType } from "@/config/types";
import { IMediaFile } from "@/shared/types";
import Button from "@components/ui/button";
import Icon from "@components/ui/icon";
import { useCreateEntity } from "@editor/hooks";
import { cn } from "@lib/cn";
import { tryInsertElementIntoActiveCell } from "@editor/modules/editor/@editor/element/options/table/hooks";
import { getActiveEntityPayload } from "@providers/custom-dnd-provider/utils";
import { ComponentType, ReactNode, memo } from "react";
import { useMediaLibDialogCtx } from "../../../ctx";
import { TElement } from "../../../types";
import { makeImageElementFromMediaFile } from "../../images-panel/utils/make-images-panel-image-element";
import { DOTS_PATTERN } from "../constants";
import { useAiImageStoreShallow } from "../store";

export const AiImageGeneratedPreview = ({
  elementWrapper,
  mediaFile,
  setMediaFile,
  handleGenerateImage,
  onSelect,
  view,
}: {
  elementWrapper?:
    | ComponentType<{
        children: ReactNode;
        element: TElement;
        className?: string;
      }>
    | undefined;
  mediaFile: IMediaFile;
  setMediaFile: (mediaFile: IMediaFile | null) => void;
  handleGenerateImage: () => void;
  onSelect: (mediaFile: IMediaFile) => void;
  view: "dialog" | "panel";
}) => {
  const { type } = useAiImageStoreShallow((s) => ({
    type: s.type,
  }));
  const { handleCreateEntity } = useCreateEntity();
  const { source } = useMediaLibDialogCtx();

  const handleUseImage = () => {
    if (!mediaFile) return;
    if (view === "dialog") {
      onSelect(mediaFile);
      return;
    }
    const madeElement = makeImageElementFromMediaFile({
      mediaFile,
    });
    // Tables 2.0 in-cell elements: with a single table cell selected, the
    // generated image inserts INTO that cell instead of below the table.
    if (tryInsertElementIntoActiveCell(madeElement)) return;

    const element = madeElement.json;
    const entity = getActiveEntityPayload({ element });
    if (!entity) return;
    const data = (entity.payload || element) as StaticEntityType;

    handleCreateEntity({
      payload: data,
      entity: {
        id: entity.id,
        type: entity.type,
        name: entity.name,
        parentId: entity.parentId,
      },
      direction: "bottom",
    });
  };

  return (
    <div>
      <div
        className="tw-flex tw-h-[376px] tw-p-8 tw-justify-center tw-items-center tw-rounded-2xl tw-bg-blue-50"
        style={{
          backgroundImage: `url("${DOTS_PATTERN}")`,
          backgroundSize: "15px 15px",
        }}
      >
        <Wrapper mediaFile={mediaFile} elementWrapper={elementWrapper}>
          <img
            src={mediaFile.url}
            alt="Generated Preview"
            className={cn(
              "tw-max-w-full tw-max-h-full tw-w-auto tw-h-auto tw-object-contain tw-rounded-lg tw-border tw-border-blue/10 tw-border-solid tw-shadow-md",
              {
                "tw-shadow-none tw-border-0":
                  type === "icon" || type === "illustration",
              },
            )}
          />
        </Wrapper>
      </div>
      <div className="tw-flex tw-items-center tw-gap-3 tw-justify-center tw-mt-6">
        {source !== "settings" && (
          <Button aria-label="Use this image" onClick={handleUseImage}>
            <Icon icon="ri-add-line" className="tw-mr-1.5" />
            Use this image
          </Button>
        )}
        <Button
          aria-label="Regenerate"
          variant="secondary"
          onClick={() => {
            handleGenerateImage();
            setMediaFile(null);
          }}
        >
          <Icon icon="ri-restart-line" className="tw-mr-1.5" />
          Regenerate
        </Button>
        <Button
          aria-label="Change prompt"
          variant="secondary"
          onClick={() => setMediaFile(null)}
        >
          <Icon icon="ri-edit-line" className="tw-mr-1.5" />
          Change prompt
        </Button>
      </div>
    </div>
  );
};

const Wrapper = memo(
  ({
    children,
    mediaFile,
    elementWrapper,
  }: {
    children: React.ReactNode;
    mediaFile: IMediaFile;
    elementWrapper?:
      | ComponentType<{
          children: ReactNode;
          element: TElement;
          className?: string;
        }>
      | undefined;
  }) => {
    if (!elementWrapper) return <>{children}</>;
    const ElementWrapper = elementWrapper;

    return (
      <ElementWrapper
        element={makeImageElementFromMediaFile({ mediaFile })}
        className="tw-h-full"
      >
        {children}
      </ElementWrapper>
    );
  },
);
